import { requestScenarioCostExport } from "routes/scenarios/controller/request-scenario-cost-export"
import { getScenarioCostExportStatus } from "routes/scenarios/controller/scenario-cost-export-status"
import { delay } from "lib/delay"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import { infoSnackbar } from "lib/snackbar/info-snackbar"
import { successSnackbar } from "lib/snackbar/success-snackbar"
import { warningSnackbar } from "lib/snackbar/warning-snackbar"

const POLL_INTERVAL_MS = 2000
// Generous enough to cover a large regime plus a queue backlog. If it is still
// exceeded the export has NOT failed: the job keeps running server-side and its
// workbook is retained, so say so rather than implying the work was lost. There is
// no export-history surface yet, so the only recovery today is to export again.
const MAX_WAIT_MS = 30 * 60 * 1000

/**
 * Database-backed export flow: queue the server-side export job, poll its status,
 * then download the finished workbook through the authenticated /s3 route. The
 * browser never rebuilds the full detail dataset.
 */
export async function runScenarioCostExport({ scenarioId, variantIds, mode }) {
    infoSnackbar("Generating export... it will download automatically when ready.")
    let jobId
    try {
        jobId = await requestScenarioCostExport({ scenarioId, variantIds, mode })
    } catch (error) {
        errorSnackbar(error.message || "Could not start the export")
        return false
    }

    const started = Date.now()
    while (Date.now() - started < MAX_WAIT_MS) {
        await delay(POLL_INTERVAL_MS)
        let status
        try {
            status = await getScenarioCostExportStatus({ jobId })
        } catch (error) {
            errorSnackbar(error.message || "Could not check the export")
            return false
        }
        if (status?.error) {
            errorSnackbar(status.error)
            return false
        }
        if (status?.status === "complete" && status.url) {
            downloadExport(status)
            successSnackbar("Export ready")
            return true
        }
        if (status?.status === "error") {
            errorSnackbar("The export failed — please try again")
            return false
        }
    }
    warningSnackbar("This export is still being generated. Leave it with us and export again if it does not arrive.")
    return false
}

function downloadExport({ url, filename }) {
    const anchor = document.createElement("a")
    anchor.href = `${url}?filename=${encodeURIComponent(filename || "cost-export.xlsx")}`
    anchor.download = filename || "cost-export.xlsx"
    document.body.appendChild(anchor)
    anchor.click()
    anchor.remove()
}
